import { Text } from "@chakra-ui/react";
import moment from "moment";
import { useContext } from "react";

import { capitalizeFirst, objectEntries } from "@nens/shared-utils";

import { BasicInfoList } from "@/components/basic-info-list";
import { ReportMap } from "@/features/report-map/components/report-map";
import { ProjectContext } from "@/stores/project-provider";
import { isBasisLabel, isOptimalisationLabel } from "@/utils/get-label-utils";

import { REPORT_BLOCK_GAP } from "../consts/sizes";

import { ReportBlock } from "./report-block";
import { ReportPage } from "./report-page";
import { ReportPageLandingCategories } from "./report-page-landing-categories";

export const ReportPageLanding = () => {
  const { project } = useContext(ProjectContext);

  // Remove "organisatie" from extraValues, because we now have it in a readOnly backend field "organisationName".
  const extraValuesArray = project.extraValues
    ? objectEntries(project.extraValues).filter(([key]) => key !== "organisatie")
    : [];

  const projectInfo = [
    { label: "Naam", value: project.name },
    { label: "Datum", value: moment(project.createdAt).format("DD-MM-YYYY") },
    { label: "Organisatie", value: project.organisationName },
    ...extraValuesArray.map(([key, value]) => ({
      label: capitalizeFirst(String(key))?.replace("_", " ") + "",
      value: typeof value === "string" ? value : undefined,
    })),
  ];

  return (
    <ReportPage>
      <ReportBlock h="600px">
        <ReportBlock.Small title="Project">
          <BasicInfoList infos={projectInfo} gap={REPORT_BLOCK_GAP} />
        </ReportBlock.Small>
        <ReportBlock.Large title={undefined} isMap>
          <ReportMap mapResult={undefined} mapId="landing-map" />
        </ReportBlock.Large>
      </ReportBlock>
      <ReportBlock>
        <ReportBlock.Large title="Inleiding">
          <Text>
            Dit rapport is bedoeld om te gebruiken voor de EU Taxonomie richtlijn binnen
            bouwprojecten. Daarnaast geeft het inzicht in de klimaatrisico's voor het aangewezen
            projectgebied aan de hand van de volgende thema's: Wateroverlast (neerslag),
            Overstromingen (vanuit zee of rivier), Hitte, Droogte en Biodiversiteit. Ook zijn
            kenmerken van het huidige gebied afgeleid. Dit helpt om een eerste beoordeling te maken
            over de gevoeligheid van het projectgebied voor klimaatverandering alsook de
            uitvoerbaarheid van een toekomstige ontwikkeling in het gebied. Het projectgebied wordt
            op de verschillende thema's gescoord door middel van een label. Deze labels zijn zo veel
            als mogelijk gebaseerd op het bestaande BlueLabel en de methodiek voor de omgevingsscore
            opgesteld binnen het Framework Climate Adaptive Buildings van DGBC (Dutch Green Building
            Council). In het rapport worden per thema voor meerdere subthema's labels berekend om
            inzicht te geven in de hoogte van het klimaatrisico. Label A staat gelijk aan een laag
            risico, Label E aan een hoog risico. Voor sommige subthema’s kunnen binnen een
            projectontwikkeling adaptatiemaatregelen worden toegepast waarmee het label in de
            toekomst (na de ontwikkeling) verbeterd kan worden. Deze labels zijn hieronder
            samengevat in het <Text as="strong">Optimalisatielabel</Text>. Andere subthema's zijn
            moeilijk te beïnvloeden binnen de uitvoering van een project en gelden als harde
            randvoorwaardes. Deze zijn hieronder samengevat in het{" "}
            <Text as="strong">Basislabel</Text>.
          </Text>
        </ReportBlock.Large>
      </ReportBlock>
      <ReportBlock>
        <ReportBlock.Large title="Basislabel">
          <ReportPageLandingCategories
            isLabelType={isBasisLabel}
            description={
              "Het basislabel is het gemiddelde label van de onderstaande subthema's. Deze subthema's zijn tijdens de ontwerpfase lastiger te beïnvloeden binnen een projectontwikkeling en gelden daarmee als hardere randvoowaardes dan de labels van het optimalisatielabel."
            }
          />
        </ReportBlock.Large>
        <ReportBlock.Large title="Optimalisatielabel">
          <ReportPageLandingCategories
            isLabelType={isOptimalisationLabel}
            description={
              "Het optimalisatielabel is het gemiddelde label van de onderstaande subthema's. De labelscore voor deze subthema's zijn te verbeteren binnen een projectontwikkeling door het toepassen van adaptatiemaatregelen."
            }
          />
        </ReportBlock.Large>
      </ReportBlock>
    </ReportPage>
  );
};
